import styled, { keyframes, css } from 'styled-components';
import { FloatingButtonsPosition } from '../../../context/chat-widget-context/types';
import { TextButton } from '@oneaudi/unified-web-components';

// Keyframes for fade in from left
const fadeInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Keyframes for fade in from right
const fadeInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Keyframes for fade out to left
const fadeOutToLeft = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-30px);
  }
`;

// Keyframes for fade out to right
const fadeOutToRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(30px);
  }
`;

export const Container = styled.div<{
  position: FloatingButtonsPosition;
  isVisible: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.position.left ? 'flex-start' : 'flex-end')};
  gap: 12px;
  position: fixed;
  bottom: ${(props) => props.position.bottom || '32px'};
  ${(props) => (props.position.left ? `left: ${props.position.left};` : '')}
  ${(props) => (props.position.right ? `right: ${props.position.right};` : '')}
  width: max-content;
  z-index: 1000;
  padding-top: 12px;
  ${(props) => (props.position.left ? 'padding-left: 12px;' : 'padding-right: 12px;')}
  pointer-events: ${(props) => (props.isVisible ? 'auto' : 'none')};
`;

export const ActionButton = styled(TextButton)<{
  position: string;
  isDisabled?: boolean;
  variant: 'primary' | 'secondary' | 'tertiary';
  isVisible?: boolean;
  animationDelay?: string;
}>`
  border-radius: 8px;
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition:
    transform 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;
  text-align: left;
  width: fit-content;
  font-family: 'Audi Type', Arial, sans-serif;
  /* Base opacity to avoid flickering before animations start */
  opacity: ${(props) => {
    if (props.isVisible) return 0; // Prepare for fade-in (start state)
    return props.isDisabled ? 0.6 : 1; // Base state or prepare for fade-out
  }};
  background-color: ${(props) => (props.variant === 'primary' ? '#fff' : '#000')};
  color: ${(props) => (props.variant === 'primary' ? '#000' : '#fff')};

  /* Apply animations based on position and visibility */
  ${(props) => {
    if (props.isVisible === false) {
      // Fade out
      return props.position === 'left'
        ? css`
            animation: ${fadeOutToLeft} 0.3s ease-out forwards;
            animation-delay: ${props.animationDelay || '0s'};
          `
        : css`
            animation: ${fadeOutToRight} 0.3s ease-out forwards;
            animation-delay: ${props.animationDelay || '0s'};
          `;
    } else {
      // Fade in
      return props.position === 'left'
        ? css`
            animation: ${fadeInFromLeft} 0.4s ease-out forwards;
            animation-delay: ${props.animationDelay || '0s'};
          `
        : css`
            animation: ${fadeInFromRight} 0.4s ease-out forwards;
            animation-delay: ${props.animationDelay || '0s'};
          `;
    }
  }}

  &:hover {
    ${(props) =>
      !props.isDisabled &&
      `
      transform: translateX(${props.position === 'left' ? '5px' : '-5px'});
    `}
    background-color: ${(props) => (props.variant === 'primary' ? '#dfdfdfff' : '#000')};
    color: ${(props) => (props.variant === 'primary' ? '#000' : '#dfdfdfff')};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const ErrorText = styled.span`
  color: #7e0000ff;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const LoadingText = styled.span`
  color: #666666;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const CloseButton = styled.button<{
  isVisible: boolean;
  itemPosition: 'left' | 'right';
}>`
  position: absolute;
  top: -16px;
  ${(props) => (props.itemPosition === 'right' ? 'left: -8px;' : 'right: -8px;')}
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #000000;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1010;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  transform: scale(${(props) => (props.isVisible ? 1 : 0.8)});
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    visibility 0.2s,
    background-color 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &:hover {
    background-color: #333333;
    transform: scale(1.1);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 1.5px;
    background-color: #ffffff;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;
